import React, { useEffect } from 'react';

import {
  searchBestRated,
  searchCaterers,
  searchMostBooked,
} from 'lib/algolia/client';
import {
  fetchCountryLandingPageBySlug,
  fetchCustomerLandingPageBySlug,
  fetchDynamicLandingPageBySlug,
  fetchDynamicPageBySlug,
  fetchMarketingPageSlugs,
  fetchSEAPageBySlug,
  fetchSearchLandingPageBySlug,
} from 'lib/contentful/landingPages';
import { fetchTestimonials } from 'lib/contentful/testimonials';
import { INDEX_LOCALES, LOCALES } from 'shared/constants';
import { CUSTOMER_LANDING_PAGE_SLUGS } from 'shared/constants';
import {
  cityKeyFromURLSlug,
  findCitySlug,
  localizedCityFromURLSlug,
  persistCityInLocalStorage,
} from 'shared/helpers/cities';
import { handleStaticPaths } from 'shared/helpers/handleStaticPaths';
import { fetchRatings } from 'shared/services/trustedShops/fetchRatings';
import DynamicPage from 'static_pages/dynamic_page/index';
import SearchLandingPage from 'static_pages/marketing_pages/components/SearchLandingPage';
import CountryLandingPage from 'static_pages/marketing_pages/country_landing_page/index';
import CustomerLandingPage from 'static_pages/marketing_pages/customer_landing_page/index';
import DynamicLandingPage from 'static_pages/marketing_pages/dynamic_landing_page/index';
import SEAPage from 'static_pages/marketing_pages/SEAPage';

const pageTypeComponentMapping = {
  SEARCH_LANDING_PAGE: SearchLandingPage,
  SEA_PAGE: SEAPage,
  COUNTRY_LANDING_PAGE: CountryLandingPage,
  DYNAMIC_LANDING_PAGE: DynamicLandingPage,
  CUSTOMER_LANDING_PAGE: CustomerLandingPage,
  DYNAMIC_PAGE: DynamicPage,
};

function MarketingPageFactory({ page, type, ...restprops }) {
  persistCityInLocalStorage(findCitySlug(page.cityFromSlug));
  const Component = pageTypeComponentMapping[type];

  useEffect(() => {
    document.body.dataset.pageType = type ?? '';
    return () => {
      document.body.dataset.pageType = '';
    };
  }, [type]);

  if (!Component) {
    throw new Error(
      `[MarketingPageFactory]: Page type not found for type: ${type}`
    );
  }

  return <Component page={page} {...restprops} />;
}

export async function getStaticProps({ params, locale }) {
  const cityFromSlug = localizedCityFromURLSlug({ slug: params.slug, locale });
  const cityKey = cityKeyFromURLSlug({ slug: params.slug, locale });
  const [searchLandingPage, ratings] = await Promise.all([
    fetchSearchLandingPageBySlug({
      slug: params.slug,
    }),
    fetchRatings(),
  ]);
  if (searchLandingPage) {
    const hits = await searchCaterers(searchLandingPage.topCaterersSearchTerm, {
      filters: `cities:"${searchLandingPage.topCaterersSearchCity}"`,
      hitsPerPage: 9,
    });
    return {
      props: {
        page: {
          ...searchLandingPage,
          topCaterers: hits,
          cityFromSlug,
        },
        ratings,
        type: 'SEARCH_LANDING_PAGE',
      },
    };
  }

  const countryLandingPage = await fetchCountryLandingPageBySlug({
    slug: params.slug,
  });
  if (countryLandingPage) {
    return {
      props: {
        page: {
          ...countryLandingPage,
          cityFromSlug,
        },
        ratings,
        type: 'COUNTRY_LANDING_PAGE',
      },
    };
  }

  const customerLandingPage = await fetchCustomerLandingPageBySlug({
    slug: params.slug,
    locale,
  });

  if (customerLandingPage) {
    return {
      props: {
        page: {
          ...customerLandingPage,
          cityFromSlug,
        },
        ratings,
        type: 'CUSTOMER_LANDING_PAGE',
      },
    };
  }

  const dynamicPage = await fetchDynamicPageBySlug({
    slug: params.slug,
  });

  const cityFilter = {
    ...(cityFromSlug && { filters: `cities:"${cityFromSlug}"` }),
  };

  if (dynamicPage) {
    return {
      props: {
        page: {
          ...dynamicPage,
          cityFromSlug: cityFromSlug,
        },
        ratings,
        type: 'DYNAMIC_PAGE',
      },
    };
  }

  const dynamicLandingPage = await fetchDynamicLandingPageBySlug({
    slug: params.slug,
  });

  if (dynamicLandingPage) {
    // TODO: remove deprecated keyword support, when contentful is ready
    const {
      section2SearchKeywords,
      section2SearchKeyword: section2DeprecatedKeyword,
      section3SearchKeywords,
      section3SearchKeyword: section3DeprecatedKeyword,
    } = dynamicLandingPage;

    const TOP_RATED_CARDS = {
      MAX_PAGES: 3,
      MAX_ITEMS_PER_PAGE: 4,
    };

    const hitsRatedPromise = searchBestRated(
      section2SearchKeywords || section2DeprecatedKeyword,
      {
        ...cityFilter,
        hitsPerPage:
          TOP_RATED_CARDS.MAX_PAGES * TOP_RATED_CARDS.MAX_ITEMS_PER_PAGE,
      }
    );

    const hitsBookedPromise = searchMostBooked(
      section3SearchKeywords || section3DeprecatedKeyword,
      {
        ...cityFilter,
        hitsPerPage: 6,
      }
    );

    const [hitsRated, hitsBooked] = await Promise.all([
      hitsRatedPromise,
      hitsBookedPromise,
    ]);

    return {
      props: {
        page: {
          ...dynamicLandingPage,
          bestRatedCaterers: hitsRated,
          mostBookedCaterers: hitsBooked,
          cityFromSlug: cityFromSlug,
        },
        ratings,
        type: 'DYNAMIC_LANDING_PAGE',
      },
    };
  }

  const [seaPage, testimonials] = await Promise.all([
    fetchSEAPageBySlug({ slug: params.slug }),
    fetchTestimonials(),
  ]);

  return {
    props: {
      page: { cityFromSlug },
      meta: {
        description: seaPage.metaDescription,
        title: seaPage.seoTitle,
      },
      hero: {
        title: seaPage.heroTitle,
        city: cityKey,
      },
      ratings,
      testimonials,
      type: 'SEA_PAGE',
      robots: 'none',
    },
  };
}

export const getStaticPaths = handleStaticPaths(async () => {
  const {
    searchLandingPageSlugs,
    seaPageSlugs,
    countryLandingPageSlugs,
    dynamicLandingPageSlugs,
    dynamicPageSlugs,
  } = await fetchMarketingPageSlugs();

  let paths = [];
  INDEX_LOCALES.forEach((locale) => {
    paths.push(
      ...searchLandingPageSlugs.map((slug) => ({
        params: {
          slug,
        },
        locale,
      }))
    );

    paths.push(
      ...countryLandingPageSlugs.map((slug) => ({
        params: {
          slug,
        },
        locale,
      }))
    );

    paths.push(
      ...seaPageSlugs.map((slug) => ({
        params: {
          slug,
        },
        locale,
      }))
    );

    paths.push(
      ...dynamicPageSlugs.map((slug) => ({
        params: {
          slug,
        },
        locale,
      }))
    );

    paths.push(
      ...dynamicLandingPageSlugs.map((slug) => ({
        params: {
          slug,
        },
        locale,
      }))
    );
  });

  LOCALES.forEach((locale) => {
    paths.push(
      ...CUSTOMER_LANDING_PAGE_SLUGS.map((slug) => ({
        params: {
          slug,
        },
        locale,
      }))
    );
  });

  return {
    paths,
    fallback: false,
  };
});

export default MarketingPageFactory;
